// src/components/SiteOverviewStatistics.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FiMoreVertical,
  FiHelpCircle,
  FiUsers,
  FiBriefcase,
  FiActivity,
} from "react-icons/fi";
import CountUp from "react-countup";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const SUMMARY_PATH = "/api/dashboard/summary";

const SiteOverviewStatistics = () => {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const language = useUserLanguage();
  const { translations } = useTranslations(language);

  const getText = useMemo(() => {
    // translations gelmediğindeyse defaultValue dön
    return (key, defaultValue) => translations[key] || defaultValue;
  }, [translations]);

  useEffect(() => {
    const url = `${API_BASE}${SUMMARY_PATH}`;
    (async () => {
      try {
        const res = await fetch(url, {
          // cookie/oturum gerekiyorsa aç:
          // credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${res.statusText} — ${txt}`);
        }
        const data = await res.json();
        setStats(data);
      } catch (e) {
        console.error("Summary fetch error:", e);
        setErr(e?.message || getText("DASHBOARD_SUMMARY_FETCH_ERROR"));
      }
    })();
  }, []);

  if (err)
    return (
      <p className="text-danger">
        {getText("DASHBOARD_ERROR_LABEL")} {err}
      </p>
    );

  if (!stats) return <p>{getText("LOADING_LABEL")}</p>;

  // Gerçek sayıların map’i (yüzde yok)
  const cards = [
    {
      id: 2,
      title: getText("DASHBOARD_USER_COUNT_TITLE"),
      total_number: stats.user_count ?? 0,
      avatarBg: "bg-success",
      icon: <FiUsers />,
      progressClass: "progress-2",
    },
  ];

  return (
    <>
      {cards.map(
        ({ id, title, total_number, avatarBg, icon, progressClass, badge }) => (
          <div key={id} className="col-xxl-3 col-md-6">
            <div className="card stretch stretch-full short-info-card">
              <div className="card-body">
                <div className="d-flex align-items-start justify-content-between mb-4">
                  <div className="d-flex gap-4 align-items-center">
                    {/* Renkli/pulse ikon alanı */}
                    <div
                      className={`avatar-text avatar-lg ${avatarBg} text-white icon`}
                    >
                      {React.isValidElement(icon) ? (
                        React.cloneElement(icon, { size: 16 })
                      ) : (
                        <FiHelpCircle size={16} />
                      )}
                    </div>

                    <div>
                      <div className="fs-4 fw-bold text-dark d-flex align-items-center gap-2">
                        {/* Yüzde YOK — canlı akış için CountUp */}
                        <span className="counter">
                          <CountUp
                            end={Number(total_number) || 0}
                            duration={1.4}
                            separator=","
                          />
                        </span>
                        {/* Küçük canlılık etiketi */}
                        <span className="badge bg-light text-muted">
                          {badge}
                        </span>
                      </div>
                      <h3 className="fs-13 fw-semibold text-truncate-1-line">
                        {title}
                      </h3>
                    </div>
                  </div>

                  <Link to="#" className="lh-1">
                    <FiMoreVertical className="fs-16" />
                  </Link>
                </div>

                {/* Alt satır — başlık tekrar + renkli/animasyonlu full bar */}
                <div className="pt-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <Link
                      to="#"
                      className="fs-12 fw-medium text-muted text-truncate-1-line"
                    >
                      {title}
                    </Link>
                    {/* Sağ tarafta yüzde/ratio yok */}
                    <div className="w-100 text-end">
                      {/* Boş bırakıyoruz veya istersen burada son güncelleme zamanı gösterebilirsin */}
                    </div>
                  </div>

                  {/* Renkli yapıyı koru: progress bar % yerine tam genişlik + animasyon */}
                  <div className="progress mt-2 ht-3">
                    <div
                      className={`progress-bar ${progressClass} progress-bar-striped progress-bar-animated`}
                      role="progressbar"
                      style={{ width: "100%" }}
                      aria-valuenow={100}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ),
      )}
    </>
  );
};

export default SiteOverviewStatistics;
